import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Star, RefreshCcw } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, DetailDrawer, formatDateTime } from "@/components/admin/ui";
import { starRow } from "./supportMeta";
import { CustomerPickerField } from "./CustomerPickerField";
import { TicketDetailDrawer } from "./TicketDetailDrawer";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";

const NewFeedbackModal = ({ API, onClose, onCreated }) => {
  const [customer, setCustomer] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!customer) return;
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/org/support/feedback`, { customer_id: customer.customer_id, rating, comment: comment.trim() || null }, { withCredentials: true });
      onCreated();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <DetailDrawer open onClose={onClose} title="Log Feedback" subtitle="Record feedback a customer gave by phone or in person." testId="new-feedback-drawer">
      <div className="space-y-4" data-testid="new-feedback-form">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="new-feedback-error">{error}</p>}
        <div><label className={labelClass}>Customer</label><div className="mt-1"><CustomerPickerField API={API} value={customer} onChange={setCustomer} testId="new-feedback-customer-picker" /></div></div>
        <div>
          <label className={labelClass}>Rating</label>
          <div className="mt-1 flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)} data-testid={`new-feedback-rating-${n}`} className={`text-2xl ${n <= rating ? "text-amber-500" : "text-slate-300 dark:text-slate-600"}`}>★</button>
            ))}
          </div>
        </div>
        <div><label className={labelClass}>Comment</label><textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="new-feedback-comment" className={inputClass} /></div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="new-feedback-cancel" className={outlineBtn}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving || !customer} data-testid="new-feedback-submit" className={btn}>Save Feedback</button>
        </div>
      </div>
    </DetailDrawer>
  );
};

export const FeedbackTab = ({ API, meta, canManage, refreshKey }) => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/org/support/feedback`, { withCredentials: true }).then(({ data }) => setResult(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const review = async (id) => {
    setBusyId(id); setActionError("");
    try { await axios.patch(`${API}/org/support/feedback/${id}/review`, {}, { withCredentials: true }); load(); } catch (err) { setActionError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusyId(null); }
  };
  const convert = async (id) => {
    setBusyId(id); setActionError("");
    try { await axios.post(`${API}/org/support/feedback/${id}/convert-to-ticket`, {}, { withCredentials: true }); load(); } catch (err) { setActionError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusyId(null); }
  };

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="support-feedback-error">{error}</p>;

  return (
    <>
      <Panel title="Feedback" subtitle={result ? `${result.summary.total} responses · Average ${result.summary.avg_rating ?? "—"}/5` : undefined} testId="support-feedback-tab"
        action={canManage && <button type="button" onClick={() => setCreating(true)} data-testid="support-feedback-new-btn" className="flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#0040CC]"><Plus size={14} /> Log Feedback</button>}>
        {actionError && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="support-feedback-action-error">{actionError}</p>}
        {!result ? <p className="text-sm text-slate-400">Loading…</p> : result.feedback.length === 0 ? (
          <EmptyState icon={Star} title="No feedback yet." message="Customer ratings and comments will show up here." testId="support-feedback-empty" />
        ) : (
          <div className="space-y-2" data-testid="support-feedback-list">
            {result.feedback.map((f) => (
              <div key={f.feedback_id} className="rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`support-feedback-row-${f.feedback_id}`}>
                <div className="flex items-center justify-between">
                  <div><span className="font-bold text-amber-500">{starRow(f.rating)}</span><span className="ml-2 text-sm font-semibold text-slate-900 dark:text-white">{f.customer_name}</span></div>
                  <span className="text-xs text-slate-400">{formatDateTime(f.created_at)} · {f.source}</span>
                </div>
                {f.comment && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">"{f.comment}"</p>}
                <div className="mt-2 flex items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${f.review_status === "reviewed" ? "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400" : "border-slate-200 text-slate-400 dark:border-white/15"}`}>{f.review_status === "reviewed" ? "Reviewed" : "New"}</span>
                  {canManage && f.review_status !== "reviewed" && <button type="button" onClick={() => review(f.feedback_id)} disabled={busyId === f.feedback_id} data-testid={`support-feedback-review-${f.feedback_id}`} className={outlineBtn}>Mark Reviewed</button>}
                  {canManage && f.rating <= 3 && (
                    f.converted_ticket_id
                      ? <button type="button" onClick={() => setSelectedTicketId(f.converted_ticket_id)} data-testid={`support-feedback-view-ticket-${f.feedback_id}`} className="text-xs font-bold text-[#0052FF] hover:underline">View {f.converted_ticket_display_id || "Ticket"}</button>
                      : <button type="button" onClick={() => convert(f.feedback_id)} disabled={busyId === f.feedback_id} data-testid={`support-feedback-convert-${f.feedback_id}`} className={outlineBtn}><RefreshCcw size={12} /> Convert to Ticket</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
      {creating && <NewFeedbackModal API={API} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {selectedTicketId && <TicketDetailDrawer API={API} ticketId={selectedTicketId} meta={meta} canManage={canManage} onClose={() => setSelectedTicketId(null)} onChanged={load} />}
    </>
  );
};
